// src/hooks/useSessions.ts

import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  fetchSessions,
  sendRemoveRequest,
  confirmRemove,
  resendPinCode,
} from '@/store/sessions/thunks';
import { UUID } from '@/types';
import {
  askEndAll,
  askEndOne,
  cancel,
  resetErrors,
} from '@/store/sessions/slice.ts';
import { useAppDispatch, useAppSelector } from '@/store/store.ts';

export const useSessions = () => {
  const { current, others, loading, step, targetIds } = useAppSelector(
    (s) => s.sessions
  );
  const targetSession = useMemo(() => {
    return targetIds?.length === 1
      ? others.find((s) => s.id === targetIds[0])
      : undefined;
  }, [others, targetIds]);
  return { current, otherSessions: others, loading, step, targetSession };
};

export const useSessionActions = () => {
  const d = useAppDispatch();

  /* stage-0: open confirm dialogs */
  const openConfirmOne = useCallback((id: UUID) => d(askEndOne(id)), [d]);
  const openConfirmAll = useCallback(() => d(askEndAll()), [d]);
  const dismiss = useCallback(() => d(cancel()), [d]);

  /* stage-1: user hit YES */
  const requestSms = useCallback(() => d(sendRemoveRequest()), [d]);

  const refresh = useCallback(() => d(fetchSessions()), [d]);

  return {
    openConfirmOne,
    openConfirmAll,
    dismiss,
    requestSms,
    refresh,
  };
};
export const usePin = () => {
  const { step, pinError, loading, resendTime } = useAppSelector(
    (s) => s.sessions
  );
  const d = useAppDispatch();
  const submitPin = useCallback(
    (code: string) => d(confirmRemove({ code })),
    [d]
  );
  const resendPin = useCallback(() => d(resendPinCode()), [d]);
  const resetError = useCallback(() => d(resetErrors()), [d]);
  const dismiss = useCallback(() => d(cancel()), [d]);

  return {
    step,
    error: pinError,
    submitPin,
    dismiss,
    loading,
    resendTime,
    resendPin,
    resetError,
  };
};

export const useInitSessions = () => {
  const dispatch = useAppDispatch();
  const hasFetched = useRef(false);

  useEffect(() => {
    if (!hasFetched.current) {
      void dispatch(fetchSessions());
      hasFetched.current = true; // guard against re-mounts in StrictMode
    }
  }, [dispatch]);
};
